import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useNumericCell } from "../../hooks/useNumericCell";
import { useCellNavigation } from "../../utils/cellNavigation";
import { formatDecimal } from "../../utils/decimalUtils";
import type { WeightCellProps } from "./types";
import { CELL_INPUT } from "./styles";
import { cn } from "@/lib/utils";
import {
  AI_PENDING_CELL_CLASS,
  getAiChangeValues,
  isAiPendingField,
} from "./AiChangeBadge";

export function WeightCell({
  flow,
  disabled,
  onFlowUpdate,
  weightPreview,
  onWeightPreview,
  proposalDiff,
}: WeightCellProps) {
  const [isFocused, setIsFocused] = useState(false);

  const aiChange = getAiChangeValues(flow, "weight");
  const aiPending = isAiPendingField(flow, "weight");
  // Explicit check on `aiChange` rather than `aiChange?.to ?? ...`: `to` can
  // be `null` (AI legitimately proposes clearing the weight), which `??`
  // would treat as absent and fall through to the legacy proposalDiff —
  // rendering the wrong suggested value.
  const proposedRaw = aiChange
    ? aiChange.to
    : proposalDiff?.fieldDiffs.weight?.newValue;
  const proposedNum =
    proposedRaw != null && proposedRaw !== "" ? Number(proposedRaw) : null;
  const effectiveWeight =
    proposedNum != null && Number.isFinite(proposedNum)
      ? proposedNum
      : (flow.weight ?? null);
  const showOldStrike =
    (aiPending || proposalDiff != null) &&
    proposedNum != null &&
    Number.isFinite(proposedNum) &&
    flow.weight != null &&
    Number(flow.weight) !== proposedNum;

  const { localValue, handleChange, handleBlur } = useNumericCell(
    effectiveWeight,
    flow.id,
    "weight",
    onFlowUpdate,
  );

  // Clear preview once the displayed weight catches up — for normal flows
  // that's `flow.weight` (blur → mutation → cache update); for proposal-driven
  // edits the new value lives in `proposalDiff.fieldDiffs.weight.newValue`,
  // so include `proposedRaw` to avoid a stale preview overriding the diff.
  useEffect(() => {
    if (weightPreview != null) {
      onWeightPreview?.(null);
    }
  }, [flow.weight, proposedRaw]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = useCellNavigation();

  // Show preview when not actively editing weight
  const displayValue =
    !isFocused && weightPreview != null
      ? formatDecimal(weightPreview)
      : localValue;

  const input = (
    <input
      type="text"
      inputMode="decimal"
      data-testid="flow-weight-input"
      data-tab-cell
      value={displayValue}
      onChange={(e) => handleChange(e.target.value)}
      onFocus={(e) => {
        setIsFocused(true);
        onWeightPreview?.(null);
        e.target.select();
      }}
      onBlur={() => {
        setIsFocused(false);
        handleBlur();
      }}
      onKeyDown={handleKeyDown}
      placeholder="-"
      disabled={disabled}
      className={cn(CELL_INPUT, showOldStrike && "text-violet-700 font-medium")}
    />
  );

  // Same wrap-always-when-aiPending logic as QuantityCell: keeps the violet
  // chrome visible when AI suggested null (legitimate "clear weight") or a
  // value equal to the current one.
  if (!showOldStrike) {
    if (!aiPending) return input;
    return (
      <span className={cn("flex items-center min-w-0", AI_PENDING_CELL_CLASS)}>
        {input}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "flex items-center gap-1 min-w-0",
        aiPending && AI_PENDING_CELL_CLASS,
      )}
    >
      <span className="text-xs text-muted-foreground line-through shrink-0">
        {formatDecimal(flow.weight)}
      </span>
      <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
      {input}
    </span>
  );
}
